// ============================================================
// THE OPPOSITION, as Stealth sees it: who is there to notice, and who
// among them is SHARPEST - the one a quiet move is tested against.
//
// "Sharpest" is the best Awareness among those present and able to notice:
// not away, not down. A Character tests its Awareness skill (half
// Perception untrained); a stat block has no skills and answers on
// Perception itself, as it does for any called test.
//
// Pure. Zero React dependency.
// ============================================================
import { calcSkillTarget } from '../characteristicTests';
import { isInTheFight, peopleOf } from '../roster/inTheFight';
import { onSide, sideOf, teamOf } from '../roster/team';
import { partySideOf } from '../phase/sides';
import type { Combatant } from '../roster/types';
import { oppStateOf } from './ladder';
import type { StealthState } from './ladder';

/**
 * THE PARTY'S SIDE, as Stealth takes it: the one the GM named; until then
 * the side of the characters the PLAYERS act as (`played`, combatant ids),
 * and failing that, of whoever this screen acts as - a kill-team's own, and
 * its allies, are not the people it is hiding from. Null when none says.
 *
 * The players' characters come before this screen's actor (2026-10-28): the
 * GM acting as an NPC turned the sides round, every pair in sight was new,
 * and one discovered brother produced dozens of the same sighting.
 */
export function partyFor(
  roster: readonly Combatant[],
  partyTeam: string | null,
  actorId: string | null,
  played: readonly string[] = [],
): string | null {
  if (partyTeam) return partySideOf(roster, partyTeam);
  const byId = (id: string | null) => (id ? roster.find((c) => c.id === id) : undefined);
  const playedSide = played.map((id) => sideOf(teamOf(byId(id)))).find(Boolean);
  return playedSide ?? sideOf(teamOf(byId(actorId)));
}

/**
 * Who the party is hiding from: everyone not on the party's team
 * (`partyFor`). With no team to go by, everyone but the one acting - which
 * is right for a lone infiltrator on a roster with no teams.
 */
export function oppositionFor(
  roster: readonly Combatant[],
  partyTeam: string | null,
  exceptId: string | null,
  played: readonly string[] = [],
): Combatant[] {
  // An Object hides from nobody and watches nobody (2026-10-26): it is in neither the party nor the opposition.
  const people = peopleOf(roster);
  const party = partyFor(people, partyTeam, exceptId, played);
  if (party) return people.filter((c) => !onSide(c, party));
  return people.filter((c) => c.id !== exceptId);
}

/** What this one notices with, as a number: the Awareness target, or a stat block's Perception. */
export function awarenessOf(c: Combatant): number {
  if (c.kind === 'statblock') return c.sheet.characteristics.per.value;
  return calcSkillTarget(c.sheet, 'awareness').base;
}

/** Those of the opposition who could notice something: present, and still standing. */
export function watchers(opposition: readonly Combatant[], state: StealthState): Combatant[] {
  return opposition.filter((c) => isInTheFight(c) && oppStateOf(state, c.id) !== 'away');
}

/**
 * The sharpest watcher, or null when nobody is there to notice. Ties go to
 * whoever comes first on the roster, so every screen names the same one.
 */
export function sharpest(opposition: readonly Combatant[], state: StealthState): Combatant | null {
  let best: Combatant | null = null;
  for (const c of watchers(opposition, state)) {
    if (!best || awarenessOf(c) > awarenessOf(best)) best = c;
  }
  return best;
}

/**
 * The party the ladder plays for: everyone the party is not hiding from (`oppositionFor`'s complement), so the
 * tracker's bearings, the sightings and the opposition panel always agree on who is who.
 */
function partyOf(roster: readonly Combatant[], opposition: readonly Combatant[]): Combatant[] {
  const hidingFrom = new Set(opposition.map((c) => c.id));
  return peopleOf(roster).filter((c) => !hidingFrom.has(c.id));
}

/** The party members that stand on the map, with where: the tracker's and the sightings' one party. */
export function placedParty<P extends { x: number; z: number }>(
  roster: readonly Combatant[],
  opposition: readonly Combatant[],
  tokens: Readonly<Record<string, P>> | undefined,
): { id: string; at: P }[] {
  return partyOf(roster, opposition).flatMap((c) => (tokens?.[c.id] ? [{ id: c.id, at: tokens[c.id] }] : []));
}
